import calculateImageSize from "../tools/calculateImageSize";
import toDataUrl from "../tools/toDataUrl";
import errorCorrectionPercents from "../constants/errorCorrectionPercents";
import QRDot from "../figures/dot/QRDot";
import QRCornerSquare from "../figures/cornerSquare/QRCornerSquare";
import QRCornerDot from "../figures/cornerDot/QRCornerDot";
import { RequiredOptions } from "./QROptions";
import gradientTypes from "../constants/gradientTypes";
import shapeTypes from "../constants/shapeTypes";
import { QRCode, FilterFunction, Gradient, DotType } from "../types";
import { getScaledFrameConfig, frameRegistry } from "../frames";

const squareMask = [
  [1, 1, 1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 1, 1, 1, 1, 1, 1]
];

const dotMask = [
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0]
];

export default class QRSVG {
  _element: SVGElement;
  _defs: SVGElement;
  _backgroundClipPath?: SVGElement;
  _dotsClipPath?: SVGElement;
  _cornersSquareClipPath?: SVGElement;
  _cornersDotClipPath?: SVGElement;
  _options: RequiredOptions;
  _qr?: QRCode;
  _image?: HTMLImageElement;

  //TODO don't pass all options to this class
  constructor(options: RequiredOptions) {
    this._element = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    this._element.setAttribute("width", String(options.width));
    this._element.setAttribute("height", String(options.height));
    this._defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    this._element.appendChild(this._defs);

    this._options = options;
  }

  get width(): number {
    return this._options.width;
  }

  get height(): number {
    return this._options.height;
  }

  getElement(): SVGElement {
    return this._element;
  }

  cleanup(): void {
    // Clear image event listeners if image exists
    if (this._image) {
      this._image.onload = null;
      this._image.onerror = null;
      this._image = undefined;
    }

    // Clear DOM element references to allow garbage collection
    // Note: We don't clear _element and _defs as they might be referenced externally
    this._backgroundClipPath = undefined;
    this._dotsClipPath = undefined;
    this._cornersSquareClipPath = undefined;
    this._cornersDotClipPath = undefined;
    this._qr = undefined;
  }

  async drawQR(qr: QRCode): Promise<void> {
    const count = qr.getModuleCount();
    const minSize = Math.min(this._options.width, this._options.height) - this._options.margin * 2;
    const realQRSize = this._options.shape === shapeTypes.circle ? minSize / Math.sqrt(2) : minSize;
    const dotSize = Math.floor(realQRSize / count);
    let drawImageSize = {
      hideXDots: 0,
      hideYDots: 0,
      width: 0,
      height: 0
    };

    this._qr = qr;

    if (this._options.image) {
      //We need it to get image size
      await this.loadImage();
      if (!this._image) return;
      const { imageOptions, qrOptions } = this._options;
      const coverLevel = imageOptions.imageSize * errorCorrectionPercents[qrOptions.errorCorrectionLevel];
      const maxHiddenDots = Math.floor(coverLevel * count * count);

      drawImageSize = calculateImageSize({
        originalWidth: this._image.width,
        originalHeight: this._image.height,
        maxHiddenDots,
        maxHiddenAxisDots: count - 14,
        dotSize
      });
    }

    this.drawBackground();
    this.drawDots((i: number, j: number): boolean => {
      if (this._options.imageOptions.hideBackgroundDots) {
        if (
          i >= (count - drawImageSize.hideXDots) / 2 &&
          i < (count + drawImageSize.hideXDots) / 2 &&
          j >= (count - drawImageSize.hideYDots) / 2 &&
          j < (count + drawImageSize.hideYDots) / 2
        ) {
          return false;
        }
      }

      if (squareMask[i]?.[j] || squareMask[i - count + 7]?.[j] || squareMask[i]?.[j - count + 7]) {
        return false;
      }

      if (dotMask[i]?.[j] || dotMask[i - count + 7]?.[j] || dotMask[i]?.[j - count + 7]) {
        return false;
      }

      return true;
    });
    this.drawCorners();

    if (this._options.image) {
      await this.drawImage({ width: drawImageSize.width, height: drawImageSize.height, count, dotSize });
    }

    // Apply frame if specified
    await this.drawFrame();
  }

  drawBackground(): void {
    const element = this._element;
    const options = this._options;

    const rand = Math.random() * (1 - 0.6) + 0.6;

    if (element) {
      const gradientOptions = options.backgroundOptions?.gradient;
      const color = options.backgroundOptions?.color;

      if (gradientOptions || color) {
        this._createColor({
          options: gradientOptions,
          color: color,
          additionalRotation: 0,
          x: 0,
          y: 0,
          height: options.height,
          width: options.width,
          name: `background-color-${rand}`
        });
      }

      if (options.backgroundOptions?.round) {
        const size = Math.min(options.width, options.height);
        const element = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        this._backgroundClipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
        this._backgroundClipPath.setAttribute("id", `clip-path-background-color-${rand}`);
        this._defs.appendChild(this._backgroundClipPath);

        element.setAttribute("x", String((options.width - size) / 2));
        element.setAttribute("y", String((options.height - size) / 2));
        element.setAttribute("width", String(size));
        element.setAttribute("height", String(size));
        element.setAttribute("rx", String((size / 2) * options.backgroundOptions.round));

        this._backgroundClipPath.appendChild(element);
      }
    }
  }

  drawDots(filter?: FilterFunction): void {
    if (!this._qr) {
      throw "QR code is not defined";
    }

    const rand = Math.random() * (1 - 0.6) + 0.6;

    const options = this._options;
    const count = this._qr.getModuleCount();

    if (count > options.width || count > options.height) {
      throw "The canvas is too small.";
    }

    const minSize = Math.min(options.width, options.height) - options.margin * 2;
    const realQRSize = options.shape === shapeTypes.circle ? minSize / Math.sqrt(2) : minSize;
    const dotSize = Math.floor(realQRSize / count);
    const xBeginning = Math.floor((options.width - count * dotSize) / 2);
    const yBeginning = Math.floor((options.height - count * dotSize) / 2);

    this._dotsClipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
    this._dotsClipPath.setAttribute("id", `clip-path-dot-color-${rand}`);
    this._defs.appendChild(this._dotsClipPath);

    this._createColor({
      options: options.dotsOptions?.gradient,
      color: options.dotsOptions.color,
      additionalRotation: 0,
      x: 0,
      y: 0,
      height: options.height,
      width: options.width,
      name: `dot-color-${rand}`
    });

    // Use path optimization for simple dot types, individual elements for complex ones
    if (this._canUsePathOptimization(options.dotsOptions.type)) {
      this._drawDotsAsPath(count, dotSize, xBeginning, yBeginning, filter);
    } else {
      this._drawDotsIndividual(count, dotSize, xBeginning, yBeginning, options.dotsOptions.type, filter, options.data);
    }

    // Handle circle shape additional dots
    if (options.shape === shapeTypes.circle) {
      this._drawCircleShapeDots(
        count,
        dotSize,
        xBeginning,
        yBeginning,
        minSize,
        options.dotsOptions.type,
        options.data
      );
    }
  }

  _canUsePathOptimization(dotType: string): boolean {
    // Path optimization works well for simple geometric shapes without neighbor-aware logic
    const optimizableTypes = ["square", "dots"];
    return optimizableTypes.includes(dotType);
  }

  _drawDotsAsPath(
    count: number,
    dotSize: number,
    xBeginning: number,
    yBeginning: number,
    filter?: FilterFunction
  ): void {
    if (!this._qr || !this._dotsClipPath) return;

    let pathData = "";
    const dotType = this._options.dotsOptions.type;

    for (let i = 0; i < count; i++) {
      for (let j = 0; j < count; j++) {
        if (filter && !filter(i, j)) {
          continue;
        }
        if (!this._qr.isDark(i, j)) {
          continue;
        }

        const x = yBeginning + j * dotSize;
        const y = xBeginning + i * dotSize;
        pathData += this._getDotPathData(x, y, dotSize, dotType);
      }
    }

    if (pathData) {
      const pathElement = document.createElementNS("http://www.w3.org/2000/svg", "path");
      pathElement.setAttribute("d", pathData);
      this._dotsClipPath.appendChild(pathElement);
    }
  }

  _drawDotsIndividual(
    count: number,
    dotSize: number,
    xBeginning: number,
    yBeginning: number,
    dotType: DotType,
    filter?: FilterFunction,
    data?: string
  ): void {
    if (!this._qr || !this._dotsClipPath) return;

    const dot = new QRDot({ svg: this._element, type: dotType, data: data });

    for (let i = 0; i < count; i++) {
      for (let j = 0; j < count; j++) {
        if (filter && !filter(i, j)) {
          continue;
        }
        if (!this._qr.isDark(i, j)) {
          continue;
        }

        dot.draw(
          yBeginning + j * dotSize,
          xBeginning + i * dotSize,
          dotSize,
          (xOffset: number, yOffset: number): boolean => {
            if (i + xOffset < 0 || j + yOffset < 0 || i + xOffset >= count || j + yOffset >= count) return false;
            if (filter && !filter(i + xOffset, j + yOffset)) return false;
            return !!this._qr && this._qr.isDark(i + xOffset, j + yOffset);
          }
        );

        if (dot._element && this._dotsClipPath) {
          this._dotsClipPath.appendChild(dot._element);
        }
      }
    }
  }

  _drawCircleShapeDots(
    count: number,
    dotSize: number,
    xBeginning: number,
    yBeginning: number,
    minSize: number,
    dotType: DotType,
    data?: string
  ): void {
    if (!this._qr || !this._dotsClipPath) return;

    const additionalDots = Math.floor((minSize / dotSize - count) / 2);
    const fakeCount = count + additionalDots * 2;
    const xFakeBeginning = xBeginning - additionalDots * dotSize;
    const yFakeBeginning = yBeginning - additionalDots * dotSize;
    const fakeMatrix: number[][] = [];
    const center = Math.floor(fakeCount / 2);

    for (let i = 0; i < fakeCount; i++) {
      fakeMatrix[i] = [];
      for (let j = 0; j < fakeCount; j++) {
        if (
          i >= additionalDots - 1 &&
          i <= fakeCount - additionalDots &&
          j >= additionalDots - 1 &&
          j <= fakeCount - additionalDots
        ) {
          fakeMatrix[i][j] = 0;
          continue;
        }

        if (Math.sqrt((i - center) * (i - center) + (j - center) * (j - center)) > center) {
          fakeMatrix[i][j] = 0;
          continue;
        }

        //Get random dots from QR code to show it outside of QR code
        fakeMatrix[i][j] = this._qr.isDark(
          j - 2 * additionalDots < 0 ? j : j >= count ? j - 2 * additionalDots : j - additionalDots,
          i - 2 * additionalDots < 0 ? i : i >= count ? i - 2 * additionalDots : i - additionalDots
        )
          ? 1
          : 0;
      }
    }

    const dot = new QRDot({ svg: this._element, type: dotType, data: data });

    for (let i = 0; i < fakeCount; i++) {
      for (let j = 0; j < fakeCount; j++) {
        if (!fakeMatrix[i][j]) continue;

        dot.draw(
          yFakeBeginning + j * dotSize,
          xFakeBeginning + i * dotSize,
          dotSize,
          (xOffset: number, yOffset: number): boolean => {
            return !!fakeMatrix[i + xOffset]?.[j + yOffset];
          }
        );
        if (dot._element && this._dotsClipPath) {
          this._dotsClipPath.appendChild(dot._element);
        }
      }
    }
  }

  _getDotPathData(x: number, y: number, size: number, dotType: string): string {
    switch (dotType) {
      case "square":
        return `M ${x} ${y} h ${size} v ${size} h ${-size} z `;

      case "dots": {
        const cx = x + size / 2;
        const cy = y + size / 2;
        const r = size / 2;
        return `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy} A ${r} ${r} 0 0 1 ${cx - r} ${cy} z `;
      }

      default:
        // Fallback to square for any unexpected types
        return `M ${x} ${y} h ${size} v ${size} h ${-size} z `;
    }
  }

  drawCorners(): void {
    if (!this._qr) {
      throw "QR code is not defined";
    }

    const element = this._element;
    const options = this._options;

    if (!element) {
      throw "Element code is not defined";
    }

    const rand = Math.random() * (1 - 0.6) + 0.6;

    const count = this._qr.getModuleCount();
    const minSize = Math.min(options.width, options.height) - options.margin * 2;
    const realQRSize = options.shape === shapeTypes.circle ? minSize / Math.sqrt(2) : minSize;
    const dotSize = Math.floor(realQRSize / count);
    const cornersSquareSize = dotSize * 7;
    const cornersDotSize = dotSize * 3;
    const xBeginning = Math.floor((options.width - count * dotSize) / 2);
    const yBeginning = Math.floor((options.height - count * dotSize) / 2);

    [
      [0, 0, 0],
      [1, 0, Math.PI / 2],
      [0, 1, -Math.PI / 2]
    ].forEach(([column, row, rotation]) => {
      const x = xBeginning + column * dotSize * (count - 7);
      const y = yBeginning + row * dotSize * (count - 7);
      let cornersSquareClipPath = this._dotsClipPath;
      let cornersDotClipPath = this._dotsClipPath;

      if (options.cornersSquareOptions?.gradient || options.cornersSquareOptions?.color) {
        cornersSquareClipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
        cornersSquareClipPath.setAttribute("id", `clip-path-corners-square-color-${column}-${row}-${rand}`);
        this._defs.appendChild(cornersSquareClipPath);
        this._cornersSquareClipPath = this._cornersDotClipPath = cornersDotClipPath = cornersSquareClipPath;

        this._createColor({
          options: options.cornersSquareOptions?.gradient,
          color: options.cornersSquareOptions?.color,
          additionalRotation: rotation,
          x,
          y,
          height: cornersSquareSize,
          width: cornersSquareSize,
          name: `corners-square-color-${column}-${row}-${rand}`
        });
      }

      if (options.cornersSquareOptions?.type) {
        const cornersSquare = new QRCornerSquare({ svg: this._element, type: options.cornersSquareOptions.type });

        cornersSquare.draw(x, y, cornersSquareSize, rotation);

        if (cornersSquare._element && cornersSquareClipPath) {
          cornersSquareClipPath.appendChild(cornersSquare._element);
        }
      } else {
        const dot = new QRDot({ svg: this._element, type: options.dotsOptions.type });

        for (let i = 0; i < squareMask.length; i++) {
          for (let j = 0; j < squareMask[i].length; j++) {
            if (!squareMask[i]?.[j]) {
              continue;
            }

            dot.draw(
              x + i * dotSize,
              y + j * dotSize,
              dotSize,
              (xOffset: number, yOffset: number): boolean => !!squareMask[i + xOffset]?.[j + yOffset]
            );

            if (dot._element && cornersSquareClipPath) {
              cornersSquareClipPath.appendChild(dot._element);
            }
          }
        }
      }

      if (options.cornersDotOptions?.gradient || options.cornersDotOptions?.color) {
        cornersDotClipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
        cornersDotClipPath.setAttribute("id", `clip-path-corners-dot-color-${column}-${row}-${rand}`);
        this._defs.appendChild(cornersDotClipPath);
        this._cornersDotClipPath = cornersDotClipPath;

        this._createColor({
          options: options.cornersDotOptions?.gradient,
          color: options.cornersDotOptions?.color,
          additionalRotation: rotation,
          x: x + dotSize * 2,
          y: y + dotSize * 2,
          height: cornersDotSize,
          width: cornersDotSize,
          name: `corners-dot-color-${column}-${row}-${rand}`
        });
      }

      if (options.cornersDotOptions?.type) {
        const cornersDot = new QRCornerDot({
          svg: this._element,
          type: options.cornersDotOptions.type,
          color: options.cornersDotOptions.color ?? options.dotsOptions.color
        });

        cornersDot.draw(x + dotSize * 2, y + dotSize * 2, cornersDotSize, rotation);

        if (cornersDot._element && cornersDotClipPath) {
          cornersDotClipPath.appendChild(cornersDot._element);
        }
      } else {
        const dot = new QRDot({ svg: this._element, type: options.dotsOptions.type });

        for (let i = 0; i < dotMask.length; i++) {
          for (let j = 0; j < dotMask[i].length; j++) {
            if (!dotMask[i]?.[j]) {
              continue;
            }

            dot.draw(
              x + i * dotSize,
              y + j * dotSize,
              dotSize,
              (xOffset: number, yOffset: number): boolean => !!dotMask[i + xOffset]?.[j + yOffset]
            );

            if (dot._element && cornersDotClipPath) {
              cornersDotClipPath.appendChild(dot._element);
            }
          }
        }
      }
    });
  }

  loadImage(): Promise<void> {
    return new Promise((resolve, reject) => {
      const options = this._options;
      const image = new Image();

      if (!options.image) {
        return reject("Image is not defined");
      }

      if (typeof options.imageOptions.crossOrigin === "string") {
        image.crossOrigin = options.imageOptions.crossOrigin;
      }

      this._image = image;

      // Use arrow functions to maintain context and add error handling
      image.onload = (): void => {
        resolve();
      };

      image.onerror = (): void => {
        reject(new Error("Failed to load image"));
      };

      // Set src after event listeners to ensure they're ready
      image.src = options.image;
    });
  }

  async drawImage({
    width,
    height,
    count,
    dotSize
  }: {
    width: number;
    height: number;
    count: number;
    dotSize: number;
  }): Promise<void> {
    const options = this._options;
    const xBeginning = Math.floor((options.width - count * dotSize) / 2);
    const yBeginning = Math.floor((options.height - count * dotSize) / 2);
    const dx = xBeginning + options.imageOptions.margin + (count * dotSize - width) / 2;
    const dy = yBeginning + options.imageOptions.margin + (count * dotSize - height) / 2;
    const dw = width - options.imageOptions.margin * 2;
    const dh = height - options.imageOptions.margin * 2;

    const image = document.createElementNS("http://www.w3.org/2000/svg", "image");
    image.setAttribute("x", String(dx));
    image.setAttribute("y", String(dy));
    image.setAttribute("width", `${dw}px`);
    image.setAttribute("height", `${dh}px`);

    const imageUrl = await toDataUrl(options.image || "");

    image.setAttribute("href", imageUrl || "");

    this._element.appendChild(image);
  }

  /**
   * Check if frame rendering is needed
   */
  _shouldRenderFrame(): boolean {
    return this._options.frameOptions.type !== "none";
  }

  /**
   * Apply frame to the SVG by wrapping the QR content within a frame
   * This implements dynamic frame scaling - any size requested
   */
  async drawFrame(): Promise<void> {
    if (!this._shouldRenderFrame()) {
      return;
    }

    const frameOptions = this._options.frameOptions;
    const requestedWidth = this._options.width;

    try {
      // Get frame data and scaled configuration
      const frameData = frameRegistry[frameOptions.type!];
      const scaledFrame = getScaledFrameConfig(frameOptions.type!, requestedWidth);

      // Add frame color styles to defs
      const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style");
      styleElement.textContent = `
        .st0 { fill: ${frameOptions.primaryColor}; }
        .st1 { fill: ${frameOptions.secondaryColor}; }
      `;
      this._defs.appendChild(styleElement);

      // Use the frame content directly (without scaling SVG wrapper)
      const frameContainer = document.createElementNS("http://www.w3.org/2000/svg", "g");
      frameContainer.setAttribute("class", "qr-frame");
      frameContainer.innerHTML = frameData.svgContent;

      // Create a container for the QR content and scale it to fit
      const qrContainer = document.createElementNS("http://www.w3.org/2000/svg", "g");
      qrContainer.setAttribute("class", "qr-content");

      // Position and scale the QR content within the frame using original coordinates
      const qrX = frameData.referenceWidth * frameData.qrPosition.x;
      const qrY = frameData.referenceHeight * frameData.qrPosition.y;
      const qrSize = frameData.referenceWidth * frameData.qrSize;
      const qrScale = qrSize / this._options.width;

      qrContainer.setAttribute("transform", `translate(${qrX}, ${qrY}) scale(${qrScale})`);

      // Move all existing QR content into the container
      const children = Array.from(this._element.children);
      children.forEach((child) => {
        if (child !== this._defs && child !== frameContainer) {
          qrContainer.appendChild(child);
        }
      });

      // Update SVG dimensions but keep original frame coordinate system

      this._element.setAttribute("width", scaledFrame.width.toString());
      this._element.setAttribute("height", scaledFrame.height.toString());
      this._element.setAttribute("viewBox", `0 0 ${frameData.referenceWidth} ${frameData.referenceHeight}`);

      // Add frame and QR content to the main SVG
      this._element.appendChild(frameContainer);
      this._element.appendChild(qrContainer);
    } catch (error) {
      console.warn(`Failed to render frame: ${error}`);
      // Continue without frame if there's an error
    }
  }

  _createColor({
    options,
    color,
    additionalRotation,
    x,
    y,
    height,
    width,
    name
  }: {
    options?: Gradient;
    color?: string;
    additionalRotation: number;
    x: number;
    y: number;
    height: number;
    width: number;
    name: string;
  }): void {
    const size = width > height ? width : height;
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(y));
    rect.setAttribute("height", String(height));
    rect.setAttribute("width", String(width));
    rect.setAttribute("clip-path", `url('#clip-path-${name}')`);

    if (options) {
      let gradient: SVGElement;
      if (options.type === gradientTypes.radial) {
        gradient = document.createElementNS("http://www.w3.org/2000/svg", "radialGradient");
        gradient.setAttribute("id", name);
        gradient.setAttribute("gradientUnits", "userSpaceOnUse");
        gradient.setAttribute("fx", String(x + width / 2));
        gradient.setAttribute("fy", String(y + height / 2));
        gradient.setAttribute("cx", String(x + width / 2));
        gradient.setAttribute("cy", String(y + height / 2));
        gradient.setAttribute("r", String(size / 2));
      } else {
        const rotation = ((options.rotation || 0) + additionalRotation) % (2 * Math.PI);
        const positiveRotation = (rotation + 2 * Math.PI) % (2 * Math.PI);
        let x0 = x + width / 2;
        let y0 = y + height / 2;
        let x1 = x + width / 2;
        let y1 = y + height / 2;

        if (
          (positiveRotation >= 0 && positiveRotation <= 0.25 * Math.PI) ||
          (positiveRotation > 1.75 * Math.PI && positiveRotation <= 2 * Math.PI)
        ) {
          x0 = x0 - width / 2;
          y0 = y0 - (height / 2) * Math.tan(rotation);
          x1 = x1 + width / 2;
          y1 = y1 + (height / 2) * Math.tan(rotation);
        } else if (positiveRotation > 0.25 * Math.PI && positiveRotation <= 0.75 * Math.PI) {
          y0 = y0 - height / 2;
          x0 = x0 - width / 2 / Math.tan(rotation);
          y1 = y1 + height / 2;
          x1 = x1 + width / 2 / Math.tan(rotation);
        } else if (positiveRotation > 0.75 * Math.PI && positiveRotation <= 1.25 * Math.PI) {
          x0 = x0 + width / 2;
          y0 = y0 + (height / 2) * Math.tan(rotation);
          x1 = x1 - width / 2;
          y1 = y1 - (height / 2) * Math.tan(rotation);
        } else if (positiveRotation > 1.25 * Math.PI && positiveRotation <= 1.75 * Math.PI) {
          y0 = y0 + height / 2;
          x0 = x0 + width / 2 / Math.tan(rotation);
          y1 = y1 - height / 2;
          x1 = x1 - width / 2 / Math.tan(rotation);
        }

        gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
        gradient.setAttribute("id", name);
        gradient.setAttribute("gradientUnits", "userSpaceOnUse");
        gradient.setAttribute("x1", String(Math.round(x0)));
        gradient.setAttribute("y1", String(Math.round(y0)));
        gradient.setAttribute("x2", String(Math.round(x1)));
        gradient.setAttribute("y2", String(Math.round(y1)));
      }

      options.colorStops.forEach(({ offset, color }: { offset: number; color: string }) => {
        const stop = document.createElementNS("http://www.w3.org/2000/svg", "stop");
        stop.setAttribute("offset", `${100 * offset}%`);
        stop.setAttribute("stop-color", color);
        gradient.appendChild(stop);
      });

      rect.setAttribute("fill", `url('#${name}')`);
      this._defs.appendChild(gradient);
    } else if (color) {
      rect.setAttribute("fill", color);
    }

    this._element.appendChild(rect);
  }
}
